import { Component, OnInit, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { NavbarComponent } from '../navbar/navbar.component';
import { LoadingSpinnerComponent } from '../shared/loading-spinner.component';
import { SchedulerPassportCellComponent } from './scheduler-passport-cell.component';
import { SchedulerStatusTabsComponent } from './scheduler-status-tabs.component';
import { SearchService } from '../shared/search.service';
import { FilterService } from '../shared/filter.service';
import { PassportService, PassportRecord } from '../shared/passport.service';
import { PassportDetailsStore } from '../shared/passport-details.store';
import { UiStateService } from '../shared/ui-state.service';
import { OfflineStorageService } from '../shared/offline-storage.service';
import { CommentsNotificationService } from '../shared/comments-notification.service';
import { PassportHelperService } from '../shared/passport-helper.service';
import { PersonaRoutingService } from '../shared/persona-routing.service';
import { BaseTabPersonaComponent } from '../shared/base-tab-persona.component';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { DateWithOrdinalPipe } from '../shared/date-with-ordinal.pipe';
import { NoPassportsComponent } from '../shared/no-passports.component';
import { FormsModule } from '@angular/forms';
import { CommentsPanelService } from '../shared/comments-panel.service';
import { HistoryPanelService } from '../shared/history-panel.service';
import { PassportsDesktopTableComponent } from '../shared/passports-desktop-table.component';
import { PERSONA_NAVBAR_ITEMS } from '../shared/constants/navbar.constant';
import {
  clearSchedulerListRestoreState,
  normalizeSchedulerDesktopShowRows,
  parseSchedulerListSnapshot,
  peekExpectSchedulerRestore,
  saveSchedulerListSnapshot,
  type SchedulerListSnapshot
} from './scheduler-list-state.storage';

/**
 * Scheduler persona component
 * Extends BaseTabPersonaComponent and adds authorised users caching
 */
@Component({
  selector: 'app-scheduler',
  standalone: true,
  imports: [
    HeaderComponent, 
    SchedulerPassportCellComponent, 
    SchedulerStatusTabsComponent, 
    CommonModule, 
    NavbarComponent, 
    LoadingSpinnerComponent, 
    DateWithOrdinalPipe, 
    NoPassportsComponent,
    FormsModule,
    PassportsDesktopTableComponent
  ],
  templateUrl: './scheduler.component.html',
  styleUrl: './scheduler.component.css'
})
export class SchedulerComponent extends BaseTabPersonaComponent implements OnInit {
  /** Navbar items constant for template */
  readonly navbarItems = PERSONA_NAVBAR_ITEMS;

  private pendingSchedulerRestore: SchedulerListSnapshot | null = null;
  private passportIdToScrollAfterRestore: string | null = null;

  protected get personaName(): string {
    return 'scheduler';
  }

  protected get defaultPersona(): string {
    return 'scheduler';
  }

  constructor(
    http: HttpClient,
    searchService: SearchService,
    filterService: FilterService,
    passports: PassportService,
    ui: UiStateService,
    detailsStore: PassportDetailsStore,
    router: Router,
    offlineStorage: OfflineStorageService,
    commentsNotification: CommentsNotificationService,
    helper: PassportHelperService,
    personaRouting: PersonaRoutingService,
    private readonly commentsPanelService: CommentsPanelService,
    private readonly historyPanelService: HistoryPanelService
  ) {
    super(http, searchService, filterService, passports, ui, detailsStore, router, offlineStorage, commentsNotification, helper, personaRouting);
  }

  override ngOnInit(): void {
    let restoring = peekExpectSchedulerRestore();
    let snapshot = restoring ? parseSchedulerListSnapshot() : null;
    if (restoring && !snapshot) {
      clearSchedulerListRestoreState();
      restoring = false;
      snapshot = null;
    }
    this.pendingSchedulerRestore = snapshot;

    if (!restoring) {
      this.filterService.clear();
      this.searchService.clear();
      this.ui.setSearchOpen(false);
      this.ui.setFiltersOpen(false);
      this.passports.records.set([]);
    }

    this.filters = this.helper.getDefaultOriginDestinationApnFilters();
    super.ngOnInit();
    this.updateDesktopViewState(true);
  }

  protected override shouldSkipDataReload(): boolean {
    if (peekExpectSchedulerRestore()) {
      return false;
    }
    return super.shouldSkipDataReload();
  }

  private applySchedulerListRestore(snapshot: SchedulerListSnapshot): void {
    this.filterService.clear();
    this.searchService.clear();
    this.ui.setSchedulerTabSelected(snapshot.tab);
    for (const key of Object.keys(snapshot.filters)) {
      const values = snapshot.filters[key];
      if (values?.length) {
        this.filterService.setFilter(key, values);
      }
    }
    this.searchService.setQuery(snapshot.searchQuery);
    this.searchService.syncFilterQueryWithQuery();
    this.rowsDropdownOpen = false;
    this.itemsPerPage = normalizeSchedulerDesktopShowRows(snapshot.itemsPerPage);
    this.sortColumn = snapshot.sortColumn;
    this.sortDirection = snapshot.sortDirection;
    this.currentPage = snapshot.currentPage;
    const totalPages = this.getTotalPages(this.visibleRecords.length);
    if (this.currentPage > totalPages) {
      this.currentPage = totalPages;
    }
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
    this.rebuildFilters();
    this.passportIdToScrollAfterRestore = snapshot.passportId;
    clearSchedulerListRestoreState();
    globalThis.setTimeout(() => {
      this.tryScrollRestoredPassportIntoView();
    }, 250);
  }

  private tryScrollRestoredPassportIntoView(): void {
    const id = this.passportIdToScrollAfterRestore;
    if (!id) {
      return;
    }
    this.passportIdToScrollAfterRestore = null;
    if (globalThis.document === undefined) {
      return;
    }
    requestAnimationFrame(() => {
      const safeId = id.replaceAll('"', '');
      const candidates = globalThis.document.querySelectorAll(`[data-passport-id="${safeId}"]`);
      const el = Array.from(candidates).find(e => (e as HTMLElement).offsetParent !== null);
      el?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    });
  }

  protected override rebuildFilters(): void {
    try {
      // Use all records (not filtered) to ensure filter options remain available
      const records = this.passports.records();
      const originOptions: string[] = [];
      const destinationOptions: string[] = [];
      const apnOptions: string[] = [];
      let hasEmptyOrigin = false;

      for (const rec of records) {
        const d = this.detailsStore.get(rec.id);
        if (!d) continue;
        
        // Check if this record has an empty origin
        if (d.origin?.trim()) {
          this.helper.addUniqueOption(originOptions, d.origin);
        } else {
          hasEmptyOrigin = true;
        }
        
        // Use weighbridge destination (from weighbridge data) with fallback to legacy destination
        const destinationValue = d.destinationWeighbridge || d.destination;
        this.helper.addUniqueOption(destinationOptions, destinationValue);
        // Prefer APN Name, fallback to APN code
        const apnValue = d.apnName || d.apn;
        this.helper.addUniqueOption(apnOptions, apnValue);
      }

      // Add "No Origin Field" option if any records have empty origins
      if (hasEmptyOrigin) {
        originOptions.unshift(this.helper.NO_ORIGIN_FIELD_TEXT);
      }

      // Sort origin options, keeping "No Origin Field" first
      const sortedOriginOptions = this.sortOriginOptionsWithNoOriginFirst(originOptions);

      this.filters = [
        { id: 'origin', label: 'Origin', options: sortedOriginOptions },
        this.helper.createFilterDefinition('destination', 'Destination', destinationOptions),
        this.helper.createFilterDefinition('apnName', 'APN/Name', apnOptions)
      ];
      console.log('Filters rebuilt (scheduler desktop):', this.filters);
    } catch (err) {
      console.warn('Error rebuilding filters:', err);
      this.filters = this.helper.getDefaultOriginDestinationApnFilters();
    }
  }

  /**
   * Sort origin options alphabetically, keeping "No Origin Field" first
   */
  private sortOriginOptionsWithNoOriginFirst(options: string[]): string[] {
    const hasNoOrigin = options.includes(this.helper.NO_ORIGIN_FIELD_TEXT);
    const otherOptions = options.filter(o => o !== this.helper.NO_ORIGIN_FIELD_TEXT);
    const sortedOthers = [...otherOptions].sort((a, b) => a.localeCompare(b));
    return hasNoOrigin ? [this.helper.NO_ORIGIN_FIELD_TEXT, ...sortedOthers] : sortedOthers;
  }

  /**
   * Override API load for scheduler persona
   */
  protected override async loadPassportsFromAPI(): Promise<void> {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    try {
      const userID = localStorage.getItem('userID');
      // FIX: Use component's personaName instead of localStorage to ensure correct roleId
      // This prevents issues when user navigates directly via URL (localStorage might have old persona)
      const persona = this.personaName; // Always use 'scheduler' for scheduler component
      const token = localStorage.getItem('accessToken');
      const tokenType = localStorage.getItem('tokenType') || 'Bearer';

      let passports: any[] = [];
      if (token) {
        try {
          const roleId = this.helper.getRoleIdForPersona(persona);
          console.log(`📋 Scheduler loading passports with persona: '${persona}', roleId: ${roleId}`);
          const apiUrl = `${environment.apiBaseUrl}/api/v1/passports?userId=${userID}&roleId=${roleId}`;
          const headers = { 'Authorization': `${tokenType} ${token}`, 'Content-Type': 'application/json' };
          passports = await firstValueFrom(this.http.get<any[]>(apiUrl, { headers }));
        } catch (e) {
          console.warn('API call failed for scheduler', e);
        }
      } else {
        console.warn('No access token found for scheduler');
      }

      if (!passports || passports.length === 0) {
        this.passports.records.set([]);
        this.rebuildFilters();
        if (this.pendingSchedulerRestore) {
          this.applySchedulerListRestore(this.pendingSchedulerRestore);
          this.pendingSchedulerRestore = null;
        }
        return;
      }

      // Map passports to frontend records with tab category based on shuntingYardID, transloadingStationID, and statusCode
      const frontendRecords: PassportRecord[] = passports.map((p: any) => {
        const tabCategory = this.helper.mapPassportToTabCategory(
          p.statusCode,
          p.shuntingYardID,
          p.transloadingStationID,
          p.loadStatus,
          p.transferFlag
        );
        return {
          id: p.passportID.toString(),
          status: 'pending',
          isSynced: true,
          tabCategory,
          statusCode: p.statusCode || '',
          transferFlag: p.transferFlag ?? false
        };
      });
      this.passports.records.set(frontendRecords);

      // Store details
      for (const p of passports) {
        this.detailsStore.upsert(this.buildPassportDetails(p));
      }

      this.rebuildFilters();
      if (this.pendingSchedulerRestore) {
        this.applySchedulerListRestore(this.pendingSchedulerRestore);
        this.pendingSchedulerRestore = null;
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Build passport details object from API passport data
   */
  private buildPassportDetails(p: any): any {
    return {
      id: p.passportID.toString(),
      loadNumber: p.loadNumber || '',
      vendorName: p.vendorName || '',
      vendorNumber: p.vendorNumber || '',
      apn: p.apn || '',
      apnName: p.apnName || '',
      destination: p.destination || '',
      destinationWeighbridge: p.destinationWeighbridge || '',
      origin: this.helper.formatOriginWithType(p.originName, p.rawSourceType),
      bulkerNumber: p.trailerNumber || '',
      bulkerMain: p.trailerMain || '',
      dueDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.dueDateTime || p.scheduledDateTime || ''),
      haulier: p.haulierName || '',
      plantID: p.plantID || p.PlantID,
      statusCode: p.statusCode || '',
      variety: p.variety || '',
      shuntingYardID: p.shuntingYardID ?? null,
      transloadingStationID: p.transloadingStationID ?? null,
      isPassportCompleted: p.isPassportCompleted ?? false,
      isCancelled: (p.loadStatusName || '').toLowerCase() === 'cancelled',
      hasSeenPcrChanges: p.hasSeenPcrChanges || false,
      loadStatusName: p.loadStatusName || '',
      loadStatus: p.loadStatus || '',
      transferFlag: p.transferFlag ?? false
    };
  }

  /**
   * Override to disable offline caching
   * Offline caching is only enabled for Grower and Transloader personas on mobile view
   */
  protected override async cacheOfflineData(headers: any): Promise<void> {
    // Scheduler does not support offline caching
    // Offline caching is only for Grower and Transloader personas on mobile view
    return;
  }

  /**
   * Cache authorised users for offline use (scheduler-specific)
   */
  private async cacheAuthorisedUsers(headers: any): Promise<void> {
    try {
      const userID = localStorage.getItem('userID');
      if (!userID) {
        console.warn('No userID found, skipping authorised users cache');
        return;
      }

      const apiUrl = `${environment.apiBaseUrl}/api/v1/authorizers?userId=${userID}`;
      console.log('Fetching authorised users from:', apiUrl);

      const response = await firstValueFrom(this.http.get<any>(apiUrl, { headers }));
      console.log('Authorised users API response:', response);

      const data = response?.authorisedUsers;
      if (data) {
        await this.offlineStorage.saveAuthorisedUsers(Number.parseInt(userID, 10), data);
        console.log(`Cached ${data.length} authorised users`);
      }
    } catch (error: any) {
      if (error?.status !== 404) {
        console.warn('Failed to cache authorised users:', error);
      }
    }
  }


  // Desktop View Getters & Methods

  /**
   * Single pass: search (debounced via filterQuery) + filters. Shared by tab counts and table.
   */
  private readonly desktopFilteredRecordsComputed = computed(() =>
    this.applyFiltersAndSearch(this.passports.records(), true)
  );

  /** Mobile: search + filters without tab restriction (debounced search via filterQuery). */
  private readonly schedulerMobileFilteredRecordsComputed = computed(() =>
    this.applyFiltersAndSearch(this.passports.records(), false)
  );

  /** Desktop rows after selected status tab (All / Load / …). */
  private readonly schedulerDesktopVisibleRecordsComputed = computed(() => {
    try {
      const selectedTab = this.ui.schedulerTabSelected();
      const filtered = this.getDesktopFilteredRecords();
      if (selectedTab === 'all' || !selectedTab) {
        return filtered;
      }
      return filtered.filter(rec => rec.tabCategory === selectedTab);
    } catch {
      return [];
    }
  });

  /** Delegates to memoized filter; tests may spy on this method. */
  private getDesktopFilteredRecords(): PassportRecord[] {
    return this.desktopFilteredRecordsComputed();
  }

  /** Delegates to memoized filter; tests may spy on this method. */
  private getMobileFilteredRecords(): PassportRecord[] {
    return this.schedulerMobileFilteredRecordsComputed();
  }

  private getDesktopVisibleRecords(): PassportRecord[] {
    return this.schedulerDesktopVisibleRecordsComputed();
  }

  override get visibleRecords(): PassportRecord[] {
    if (this.isDesktopView) {
      return this.getDesktopVisibleRecords();
    }
    return super.visibleRecords;
  }

  /**
   * Apply filters and search to records
   */
  private applyFiltersAndSearch(records: PassportRecord[], isDesktop: boolean): PassportRecord[] {
    try {
      const query = (this.searchService.filterQuery() || '').trim().toLowerCase();
      const hasQuery = query.length > 0;
      const activeFilters = this.filterService.filters();

      return records.filter(rec => {
        const det = this.detailsStore.get(rec.id);

        // Apply active filters (all filters apply to both desktop and mobile)
        const apnValue = det?.apnName || det?.apn;
        if (!this.helper.matchesFilter(activeFilters, 'apnName', apnValue)) return false;
        if (!this.helper.matchesFilter(activeFilters, 'haulier', det?.haulier)) return false;
        const destinationValue = det?.destinationWeighbridge || det?.destination;
        if (!this.helper.matchesFilter(activeFilters, 'destination', destinationValue)) return false;
        if (!this.helper.matchesFilter(activeFilters, 'origin', det?.origin)) return false;

        // Apply search query
        if (!hasQuery) return true;

        const baseFields = this.helper.buildSearchFields(rec, det);
        
        if (isDesktop) {
          return this.buildDesktopSearchHaystack(baseFields, det).includes(query);
        } else {
          // Mobile search uses base fields only
          const haystack = baseFields.join(' ').toLowerCase();
          return haystack.includes(query);
        }
      });
    } catch {
      return [];
    }
  }

  /** One pass over filtered rows for tab badges (desktop). */
  readonly schedulerDesktopTabCounts = computed(() =>
    this.aggregateSchedulerTabCounts(this.getDesktopFilteredRecords())
  );

  /** One pass over filtered rows for tab badges (mobile). */
  readonly schedulerMobileTabCounts = computed(() =>
    this.aggregateSchedulerTabCounts(this.getMobileFilteredRecords())
  );

  private aggregateSchedulerTabCounts(records: PassportRecord[]): {
    load: number;
    shunting: number;
    transloading: number;
    receiving: number;
    transferred: number;
  } {
    let load = 0;
    let shunting = 0;
    let transloading = 0;
    let receiving = 0;
    let transferred = 0;
    for (const r of records) {
      switch (r.tabCategory) {
        case 'load':
          load++;
          break;
        case 'shunting':
          shunting++;
          break;
        case 'transloading':
          transloading++;
          break;
        case 'receiving':
          receiving++;
          break;
        case 'transferred':
          transferred++;
          break;
        default:
          break;
      }
    }
    return { load, shunting, transloading, receiving, transferred };
  }

  override get allCount(): number {
    if (this.isDesktopView) {
      return this.getDesktopFilteredRecords().length;
    }
    return this.getMobileFilteredRecords().length;
  }

  override get loadCount(): number {
    if (this.isDesktopView) {
      return this.schedulerDesktopTabCounts().load;
    }
    return this.schedulerMobileTabCounts().load;
  }

  override get shuntingCount(): number {
    if (this.isDesktopView) {
      return this.schedulerDesktopTabCounts().shunting;
    }
    return this.schedulerMobileTabCounts().shunting;
  }

  override get transloadingCount(): number {
    if (this.isDesktopView) {
      return this.schedulerDesktopTabCounts().transloading;
    }
    return this.schedulerMobileTabCounts().transloading;
  }

  get receivingCount(): number {
    if (this.isDesktopView) {
      return this.schedulerDesktopTabCounts().receiving;
    }
    return this.schedulerMobileTabCounts().receiving;
  }

  override get transferredCount(): number {
    if (this.isDesktopView) {
      return this.schedulerDesktopTabCounts().transferred;
    }
    return this.schedulerMobileTabCounts().transferred;
  }

  /**
   * Override to add scheduler-specific sort columns (vendorName, passportComplete)
   */
  protected override getSortValues(column: string, a: PassportRecord, b: PassportRecord, aDetails: any, bDetails: any): { valA: any; valB: any } {
    // Handle scheduler-specific columns
    if (column === 'vendorName') {
      return {
        valA: aDetails?.vendorName || '',
        valB: bDetails?.vendorName || ''
      };
    }
    if (column === 'passportComplete') {
      return {
        valA: aDetails?.isPassportCompleted ? 'yes' : 'no',
        valB: bDetails?.isPassportCompleted ? 'yes' : 'no'
      };
    }
    // Fall back to base class for common columns
    return super.getSortValues(column, a, b, aDetails, bDetails);
  }

  get totalPages(): number {
    return this.getTotalPages(this.visibleRecords.length);
  }

  onTabSelect(tab: 'all' | 'load' | 'shunting' | 'transloading' | 'receiving' | 'transferred'): void {
    this.ui.setSchedulerTabSelected(tab);
    this.currentPage = 1;
    this.rebuildFilters();
  }


  openPassportDetails(id: string): void {
    const filtersRaw = this.filterService.filters();
    const filtersClone: Record<string, string[]> = {};
    for (const key of Object.keys(filtersRaw)) {
      const vals = filtersRaw[key];
      filtersClone[key] = vals ? [...vals] : [];
    }

    const scrollY =
      globalThis.window === undefined
        ? undefined
        : globalThis.window.scrollY || globalThis.document?.documentElement?.scrollTop || 0;

    saveSchedulerListSnapshot({
      tab: this.ui.schedulerTabSelected(),
      filters: filtersClone,
      searchQuery: this.searchService.query(),
      currentPage: this.currentPage,
      itemsPerPage: normalizeSchedulerDesktopShowRows(this.itemsPerPage),
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      passportId: id,
      scrollY: scrollY === 0 ? undefined : scrollY
    });

    this.ui.setExpandedRecord(id);
    this.scrollPositionService.saveScrollPosition('/scheduler');
    const tab = this.ui.schedulerTabSelected();
    void this.router.navigate(['/passport', id], { state: { from: 'scheduler', tab } });
  }

  /**
   * Build desktop search haystack with additional fields
   */
  private buildDesktopSearchHaystack(baseFields: string[], det: any): string {
    const dueDateTime = det?.dueDateTime || '';
    const formattedScheduled = dueDateTime
      ? this.dateTimeHelper.formatScheduledText(dueDateTime)
      : undefined;
    const scheduledText = formattedScheduled ? formattedScheduled.toLowerCase() : '';
    const combinedVendor = `${det?.vendorNumber || ''} - ${det?.vendorName || ''}`.toLowerCase();
    const statusText = (det?.statusCode || '').toLowerCase().replaceAll('_', ' ');
    const passportCompleteText = (det?.isPassportCompleted ? 'yes' : 'no');

    return [
      ...baseFields,
      scheduledText,
      combinedVendor,
      statusText,
      passportCompleteText,
      det?.vendorNumber || '',
      det?.vendorName || '',
      det?.apn || '',
      det?.apnName || '',
      det?.bulkerMain || ''
    ].join(' ').toLowerCase();
  }

  /**
   * Build header object for comments/history panels
   * Override to add scheduler-specific fields
   */
  protected override buildPanelHeader(id: string): { loadNumber: string; variety: string; haulier: string; scheduledText: string; origin: string; destination: string; apnName: string; vendorName: string; vendorNumber: string; bulkerNumber: string; isGatehouse?: boolean; isScheduler?: boolean } | undefined {
    const header = super.buildPanelHeader(id);
    if (header) {
      return { ...header, isGatehouse: false, isScheduler: true };
    }
    return undefined;
  }

  // Public methods that use the protected base class buildPanelHeader
  // These don't override the base class methods (which are protected with different signatures)
  // but TypeScript requires override modifier for same-named methods
  override openComments(id: string, commentsPanelService?: CommentsPanelService): void {
    const header = this.buildPanelHeader(id);
    if (header) {
      (commentsPanelService || this.commentsPanelService).openCommentsPanel(id, header);
    }
  }

  override openHistory(id: string, historyPanelService?: HistoryPanelService): void {
    const header = this.buildPanelHeader(id);
    if (header) {
      (historyPanelService || this.historyPanelService).openHistoryPanel(id, header);
    }
  }
}



